import { get, isArray, omitBy, isNull } from 'lodash-es';

// NU_FEATURE: prospectPdp
// https://urbnit.atlassian.net/browse/TYP-33821
import runtimeConfig from '~config/config';
import { URBN_BRANDS } from '~coreModules/core/js/constants';
import {
    FILTERED_DEPARTMENTS,
    ALLOWED_PRODUCT_REVIEW_FILTERS,
    DEFAULT_NUM_RECOMMENDATION_ITEMS,
    PRODUCT_REVIEW_FILTER_BRA_SIZES,
    PRODUCT_PAGE_VIEW_MODES,
    PLUS_SIZE_IDENTIFIER,
} from '~routes/product/js/product-constants';

import { rentalRouteNames } from '~modules/core/js/router-constants';

/**
 * Format a list of choices to be usable by a Radio/Checkbox group
 *
 * @param  {Array} choices array of choices from the product endpoint
 * @return {Array} array of value-label pairs based on the choices
 */
export function mapChoicesToRadioObject(choices) {
    return choices.map(choice => ({
        value: choice.color.code,
        label: choice.color.displayName,
    }));
}

/**
 * find the sku color code from the array of choices from the product endpoint
 *
 * @param  {Array} choices array of choices from the product endpoint
 * @param  {String} sku sku id in query parameters
 * @return {(String|null)} the color code of sku
 */
export function getSkuColor(choices, sku) {
    if (!choices || !choices.length || !sku) {
        return null;
    }

    const skuChoice = choices
        .find(choice => choice.sizeGroups
            .some(sizeGroup => sizeGroup.includedSkus
                .some(includedSku => includedSku.skuId === sku),
            ),
        );

    return skuChoice ? skuChoice.color.code : null;
}

/**
 * Format review submitted date
 * Why we use this function -- we cant accomplish the '-' delimited date that we want with vue-i18n,
 * as its not supported by `Intl.DateTimeFormat`
 *
 * @param  {String} dates string of date from the product review details
 * @return {String} format date to dd-mmm-yyyy
 */
export function formatDate(date) {
    const dateTime = new Date(date);
    return `${dateTime.getDate()}-${dateTime.toLocaleString('en-us', { month: 'short' })}-${dateTime.getFullYear()}`;
}

/**
 * Get a sku's size display name
 *
 * @param  {String} sizeGroups the size groups associated to a given product
 * @param  {String} sku sku of product
 * @return {String} size display name
 */
export function getSizeDisplayName(sizeGroups, sku) {
    const flattenedSkus = sizeGroups.flatMap(({ includedSkus }) => includedSkus);
    const matchedSku = flattenedSkus.find(flattenedSku => flattenedSku.skuId === sku);
    return get(matchedSku, 'size.displayName', '');
}

/**
 * Shape the r15 response for reviews
 *
 * @param  {Array} productReviews - reviews returned by r15 service
 * @return {Array} shaped reviews to be used in UI
 */
export function shapeProductReviews(productReviews) {
    let shapedReviews = [];
    let imageReviewIndex = 0;

    if (productReviews.length) {
        shapedReviews = productReviews.map((review) => {
            let shapedReview = review;

            if (review.photos && review.photos.length) {
                shapedReview = { ...review, ...{ imageReviewIndex } };
                imageReviewIndex++;
            }
            return shapedReview;
        });
    }

    return shapedReviews;
}

/**
 * Get allowed filter groups from r15 response
 *
 * @param  {Array} filterGroups the review filter groups
 * @return {Array} filtered filter groups
 */
export function getAllowedFilterGroups(filterGroups) {
    const isAllowedFilterGroup = filterGroup => ALLOWED_PRODUCT_REVIEW_FILTERS.includes(filterGroup.name);

    return filterGroups?.length ? filterGroups.filter(isAllowedFilterGroup) : [];
}

/**
 * Get filter groups with bra size when a bra size is included in the r15 response
 *
 * @param  {Array} filterGroups the review filter groups
 * @param  {Object} braSize the review bra size filter group object
 * @return {Array} filtered filter groups with bra size added
 */
export function getFilterGroupsWithBraSize(filterGroups, braSize) {
    if (!filterGroups?.length) return [];

    const isFilterGroupBraSizes = filterGroup => PRODUCT_REVIEW_FILTER_BRA_SIZES.includes(filterGroup.name);
    const braSizes = filterGroups.filter(isFilterGroupBraSizes);
    const shouldConcat = braSize && braSizes?.length;

    if (shouldConcat) {
        const newFilterGroup = Object.assign(braSize, {
            filterValues: braSizes,
            selectedFilters: braSizes.map(filterGroup => filterGroup.selectedFilters)
                .flat(),
        });

        return filterGroups.filter(filterGroup => !isFilterGroupBraSizes(filterGroup))
            .concat(newFilterGroup);
    }

    return filterGroups;
}

/**
 * Shapes the filterValues for a particular filterGroup
 *
 * @param {Array} filterValues the values in a filterGroup that can be filtered on
 * @param {String} selectedFilters the selected filters of the current filterGroup
 * @returns {Array} the formatted filter values with shaped values
 */
export function shapeFilterValues(filterValues, selectedFilters) {
    const shapedFilterOptions = filterValues.map(filterOption => ({
        ...filterOption,
        value: filterOption.filterValue,
        displayName: `${filterOption.filterValue} (${filterOption.count})`,
        label: filterOption.filterValue,
        disabled: !filterOption.count && !selectedFilters.includes(filterOption.filterValue),
    }));

    return shapedFilterOptions;
}

/**
 * Shapes the filterGroups into a consumable format for the UI
 *
 * @param {Array} filterGroups the review filter groups
 * @param {Object} selectedFilters the object of selected filters
 * @returns {Array} the formatted filter groups with shaped values
 */
export function shapeFilterGroups(filterGroups, selectedFilters) {
    const filteredFilterGroups = getAllowedFilterGroups(filterGroups);

    if (!filteredFilterGroups?.length) {
        return [];
    }

    return filteredFilterGroups.map((filterGroup) => {
        const selectedFilterValues = selectedFilters?.[filterGroup.name] || [];

        return {
            ...filterGroup,
            selectedFilters: selectedFilterValues,
            filterValues: shapeFilterValues(filterGroup.filterValues, selectedFilterValues),
        };
    });
}

/**
 * Shapes the selected filter into a consumable format for r15 service
 *
 * @param {Object} selectedFilters the object of selected filters
 * @returns {Array} the formatted selected filters with shaped values
 */
export function shapeSelectedFilterParams(selectedFilters) {
    const filterGroupNames = Object.keys(selectedFilters);

    return filterGroupNames
        .filter((filterGroupName) => {
            const selectedFilterValues = selectedFilters[filterGroupName];

            return isArray(selectedFilterValues) &&
                !!selectedFilterValues?.length;
        })
        .reduce((previousSelectedFilters, filterGroupName) => {
            const newSelectedFilters = { ...previousSelectedFilters };
            newSelectedFilters[filterGroupName] = selectedFilters?.[filterGroupName].join(',');

            return newSelectedFilters;
        }, {});
}

/**
 * Clears all the selected filters into consumable format for r15 service
 *
 * @param {Object} selectedFilters the object of selected filters
 * @returns {Object} the selected filters with all filter values cleared
 */
export function getClearedSelectedFilters(selectedFilters) {
    const filterGroupNames = Object.keys(selectedFilters);
    if (!filterGroupNames?.length) return {};

    const clearedSelectedFilters = filterGroupNames
        .reduce((clearedFilter, key) => ({ ...clearedFilter, [key]: [] }), {});

    return clearedSelectedFilters;
}

/**
 * Creates an array of size objects for a given start/end range
 * Start number is incremented by 2 up until the end
 * This array is typically passed to a BaseCheckboxGroup component
 *
 * @param  {Number} start - number to start the range
 * @param  {Number} end - number to end the
 * @param  {Object} params - Optional parameters
 * @param  {string=''} params.suffix - string to append to end of label and value. default is empty string
 * @param  {Boolean=false} params.hasDoubleZero - sets first label/value in array should be 00. default is false
 * @return {Array} list of size range objects containing label/value properties
 */
export function getSizeRange(start, end, { suffix = '', hasDoubleZero = false } = {}) {
    let sizes = [];
    if (hasDoubleZero) {
        if (suffix) {
            sizes = [{ label: `00 ${suffix}`, value: `00 ${suffix}` }];
        } else {
            sizes = [{ label: '00', value: '00' }];
        }
    }

    let currentSize = start;
    while (currentSize <= end) {
        if (suffix) {
            sizes.push({ label: `${currentSize} ${suffix}`, value: `${currentSize} ${suffix}` });
        } else {
            sizes.push({ label: `${currentSize}`, value: `${currentSize}` });
        }
        currentSize += 2;
    }

    return sizes;
}

/**
 * Returns the product brand display name
 *
 * @param  {Array} brands - the brandUrls list
 * @return {String} the filtered brand display name
 */
export function getProductBrandName(brands) {
    let filteredBrand;

    if (brands?.length > 1) {
        [filteredBrand] = brands.filter(brand => !URBN_BRANDS.includes(brand.displayName));
    } else {
        [filteredBrand] = brands;
    }
    return filteredBrand && filteredBrand.displayName;
}

/**
 * Returns the product department
 *
 * @param  {Array} departments - the nuulyDepartment facet list
 * @return {String} the filtered department
 */
export function getProductDepartment(departments) {
    if (!departments) return null;

    let department;

    if (departments?.length > 1) {
        department = departments.find(dept => !FILTERED_DEPARTMENTS.includes(dept));
    } else {
        [department] = departments;
    }

    return department;
}

/**
 * Creates a new array of merged products for ShopTheLook functionality where
 * a products productsRelatedToColorData is returned from the pdp call and available to rent
 *
 * @param  {Array} products - recommended products
 * @param  {Array} shopTheLookProducts - associated products
 * @return {Array} merged products with available Shop The Look products added to front of array
 */
export function shapeShopTheLookProducts(products, shopTheLookProducts) {
    const shapedProducts = shopTheLookProducts
        .map(product => ({
            ...product,
            showShopTheLookBanner: true,
        }));
    return [...shapedProducts, ...products].slice(0, DEFAULT_NUM_RECOMMENDATION_ITEMS);
}

/**
 * Returns redirect obj for PDP to be used with UNIVERSAL_REDIRECT
 * @param {Object} args - Object containing passed in args
 * @param {String} args.slug - The current product slug
 * @param {Object} args.query - query params
 * @param {Object} args.router - vue-router instance
 *
 * @returns {Object} redirect object to be used by UNIVERSAL_REDIRECT
 */
export function getPdpRedirect({
    slug,
    query,
    router,
}) {
    let newQuery = { ...query };
    newQuery = omitBy(newQuery, isNull);

    return {
        router,
        route: {
            name: rentalRouteNames.product,
            params: { slug },
            query: newQuery,
        },
        replace: false,
    };
}

/**
 * returns the correct color code and sku based on sku
 *
 * @param  {String} sku - sku code from query params
 * @param  {String} color - color code from query params
 * @param  {Array} choices - array of product choices
 * @return {Object} new color and sku query params
 */
export function updateColorAndSkuForSku(sku, color, choices) {
    const skuColorCode = getSkuColor(choices, sku);
    let newColor = color;
    let newSku = sku;

    if (skuColorCode && skuColorCode !== color) {
        // sku belongs to a different color choice, update color code
        newColor = skuColorCode;
    } else if (!skuColorCode) {
        // sku is not found in the list of choices, remove sku
        newSku = null;
    }

    return { newColor, newSku };
}

/**
 * Returns a the query params to be used for rendering pdp
 * @param {Object} args - Object containing passed in args
 * @param {String} args.choices - array of product choices
 * @param {Object} args.color - color code from query params
 * @param {Object} args.sku - sku code from query params
 * @param {String} args.view - mode to render PDP
 *
 * @returns {Object} object containing params to render pdp
 */
export function getPdpQueryParams({
    choices,
    color,
    sku,
    view,
}) {
    const isBuyMode = view === PRODUCT_PAGE_VIEW_MODES.BUY;
    let newColor = color;
    let newSku = sku;

    if (sku) {
        ({ newSku, newColor } = updateColorAndSkuForSku(sku, color, choices));
    }

    const selectedChoice = choices.find(choice => choice.color.code === newColor);
    const firstColorCode = choices?.[0]?.color?.code || '';

    if (isBuyMode) {
        const firstPrePurchaseChoice = choices.find(choice => choice.prePurchase?.eligible);
        const firstPrePurchaseColorCode = firstPrePurchaseChoice?.color?.code;

        if (!firstPrePurchaseColorCode) {
            // there are no pre-purchase eligible choices found, redirect to rent mode, use first color code
            return { color: firstColorCode, sku: null, view: null };
        }

        if (!selectedChoice || (selectedChoice && !selectedChoice?.prePurchase?.eligible)) {
            // selected color is not found in the list of returned choices or is prePurchase ineligible
            // set the first pre-purchase color as the selected color
            return { color: firstPrePurchaseColorCode, sku: null, view: PRODUCT_PAGE_VIEW_MODES.BUY };
        }
    } else if (!isBuyMode && !selectedChoice) {
        // selected color is not found in list of returned choices, update url to first color code
        return {
            color: firstColorCode,
            sku: newSku,
            view: null,
        };
    }

    return { color: newColor, sku: newSku, view };
}

/**
 * Creates a shaped product images array for plus size face outs
 * @param {Array} images - array of product images
 * @returns {Array} shaped product images array with the first image being the plus size face out
 */
export function getPlusSizeFaceOut(images = []) {
    const foundImage = images.find(image => image.includes(PLUS_SIZE_IDENTIFIER));

    return foundImage ? [foundImage, ...images.filter(image => image !== foundImage)] : images;
}

/**
 * Creates an object of choiceId with it's available sku ids
 * @param {Array} choices - array of product choices
 * @returns {Object} shaped object of choiceId with available sku ids
 */
export function getSizeCodeAvailibilityByChoiceId(choices) {
    return (choices || [])
        .reduce((result, { choiceId, sizeGroups }) => {
            const sizeCodes = (sizeGroups || [])
                .flatMap(({ includedSkus }) => includedSkus
                    .filter(({ availableInventory }) => availableInventory > 0)
                    .map(({ size }) => size.code),
                );
            // eslint-disable-next-line no-param-reassign
            result[choiceId] = sizeCodes || [];
            return result;
        }, {});
}

// NU_FEATURE: prospectPdp
// https://urbnit.atlassian.net/browse/TYP-33821
export const PROSPECT_PDP_QUERY = 'pdp';
export const PROSPECT_PDP_VALUE = 'prospect';

export const isProspectPdpParam = route => route?.query?.[PROSPECT_PDP_QUERY] === PROSPECT_PDP_VALUE;
export const isProspectPdpEnabled = route => (
    isProspectPdpParam(route) && runtimeConfig.features.prospectPdp);
